import { useTranslation } from 'react-i18next';

const colorMap = {
  blue: 'badge-blue',
  green: 'badge-green',
  red: 'badge-red',
  yellow: 'badge-yellow',
  gray: 'badge-gray',
};

const Badge = ({ children, color = 'gray', className = '' }) => {
  return <span className={`${colorMap[color] || colorMap.gray} ${className}`}>{children}</span>;
};

// Preset status badges. Culoarea rămâne fixă pe stare; eticheta se traduce prin common.status.*.
const statusConfig = {
  active: { color: 'green', key: 'common.status.active' },
  inactive: { color: 'red', key: 'common.status.inactive' },
  pending: { color: 'yellow', key: 'common.status.pending' },
  completed: { color: 'green', key: 'common.status.completed' },
  failed: { color: 'red', key: 'common.status.failed' },
  in_progress: { color: 'blue', key: 'common.status.inProgress' },
  not_started: { color: 'gray', key: 'common.status.notStarted' },
  PASSED: { color: 'green', key: 'common.status.completed' },
  FAILED: { color: 'red', key: 'common.status.failed' },
  IN_PROGRESS: { color: 'blue', key: 'common.status.inProgress' },
  NOT_STARTED: { color: 'gray', key: 'common.status.notStarted' },
};

export const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const { color, key } = statusConfig[status] || statusConfig.pending;
  return <Badge color={color}>{t(key)}</Badge>;
};

// Preset role badges. Culorile sunt fixe pe rol; eticheta refolosește cheile layout.role.*.
const roleColors = {
  SUPER_ADMIN: 'red',
  SSM_COMPANY_ADMIN: 'blue',
  SSM_INSTRUCTOR: 'blue',
  COMPANY_ADMIN: 'yellow',
  DIRECTOR_GENERAL: 'green',
  EMPLOYEE: 'gray',
};

export const RoleBadge = ({ role }) => {
  const { t } = useTranslation();
  const color = roleColors[role] || 'gray';
  // Pentru un rol necunoscut afișăm enum-ul brut (comportamentul anterior), via defaultValue.
  return <Badge color={color}>{t(`layout.role.${role}`, { defaultValue: role })}</Badge>;
};

// Afișează toate „etichetele" suplimentare ale unui utilizator (CLM, HR, Instructor SSM, Cadru PSI).
// Se folosește sub RoleBadge ca persoana să-și vadă ușor responsabilitățile active.
export const UserLabelBadges = ({ user, className = '' }) => {
  const { t } = useTranslation();
  if (!user) return null;
  const badges = [];
  // CLM: pe bază de mandat (isCLM) SAU responsabil de departament (managesDepartment).
  if (user.managesDepartment || user.isCLM)
    badges.push({ key: 'clm', color: 'blue', label: t('common.badges.clm') });
  if (user.canManageEmployees)
    badges.push({ key: 'hr', color: 'green', label: t('common.badges.hr') });
  if (user.isSSMInstructor)
    badges.push({ key: 'instr', color: 'blue', label: t('layout.role.SSM_INSTRUCTOR') });
  if (user.isCadruTehnicPSI)
    badges.push({ key: 'psi', color: 'yellow', label: t('common.badges.psi') });
  // Împuternicire de admin — NU e un rol; e o etichetă separată. Rolul real rămâne în RoleBadge.
  if (user.actsAsCompanyAdmin)
    badges.push({ key: 'emp-admin', color: 'red', label: t('common.badges.empoweredAdmin') });
  // Beneficiar EPI (echipament de protecție individuală) — atribut al angajatului.
  // Roșu, consecvent cu eticheta EPI din EmployeeDetails.
  if (user.hasEPI) badges.push({ key: 'epi', color: 'red', label: t('common.badges.epi') });
  if (!badges.length) return null;
  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {badges.map((b) => (
        <Badge key={b.key} color={b.color} className="text-[10px] font-bold">
          {b.label}
        </Badge>
      ))}
    </div>
  );
};

export default Badge;
